import {useCallback, useRef, useState} from 'react';

/**
 * Return type for {@link useAvailableWidthPx}.
 *
 * @property availableWidthPx - Remaining horizontal space from the element's
 *   left edge to the right edge of its parent container, measured in **whole
 *   CSS pixels** (value is floored).  `0` while the element has not mounted
 *   yet.
 * @property callbackRef - A React **callback ref** to place on the element
 *   whose width you want to constrain.  Unlike a plain `useRef`, a callback
 *   ref fires as soon as the DOM node appears — even when it appears *after*
 *   an initial loading/skeleton early-return — so the width is always
 *   computed.
 */
type UseAvailableWidthPxReturn = {
  availableWidthPx: number;
  callbackRef: (node: HTMLDivElement | null) => void;
};

/**
 * Calculate the remaining width from an element's left edge to the right edge
 * of its **parent container** in **raw CSS pixels**.
 *
 * ### Why this exists
 *
 * When EagGrid is placed inside a flex-row layout (e.g. a sidebar + grid
 * arrangement using `HStack`), the platform `Box` wrapper often receives
 * `width: 100%` which resolves to the entire container width instead of the
 * remaining space after the sidebar.  This causes the grid to overflow
 * horizontally beyond the layout boundary.
 *
 * This hook solves the problem inside EagGrid by measuring the true available
 * width at runtime using `getBoundingClientRect()`:
 *
 * ```
 * relativeLeft = element.rect.left − parent.rect.left
 * availableWidth = parent.clientWidth − relativeLeft
 * ```
 *
 * ### Why getBoundingClientRect and NOT offsetLeft
 *
 * `element.offsetLeft` is relative to the **offsetParent** (nearest positioned
 * ancestor), which can be far up the DOM tree — e.g. `<body>`.  When
 * EagGridScreenWrapper sits inside `<Box width="100%">` inside an `<HStack>`,
 * `offsetLeft` includes the entire left navigation sidebar (48px) + folder
 * tree (229px) + separator (33px) ≈ 310–414px.  But `parentElement.clientWidth`
 * is only the Box's width (~1011px).  The mismatch produces a far too narrow
 * result: `1011 − 414 = 597px`.
 *
 * `getBoundingClientRect().left` on both elements gives the true visual offset
 * relative to the viewport, so subtracting them yields the correct offset
 * within the parent: typically `0` when the element starts at the parent's
 * left edge.
 *
 * ### How it works
 *
 * A `ResizeObserver` on `document.body` recalculates the available width
 * whenever the page layout shifts (sidebar toggle, window resize, etc.).
 *
 * To avoid unnecessary re-renders:
 *
 * - The value is **floored** to whole pixels — subpixel jitter does not
 *   trigger a state update.
 * - A **previous-value ref** skips `setState` when the floored result is
 *   identical to the last stored value.
 *
 * The only `useCallback` in this hook stabilises the callback ref so React
 * doesn't detach/reattach it on every render.
 *
 * ### Pure helper
 *
 * The arithmetic is extracted into {@link calculateRemainingWidth} for unit
 * testing without a DOM.
 *
 * @example
 * ```tsx
 * function EagGridScreenWrapper(props: EagGridScreenWrapperProps) {
 *   const {availableWidthPx, callbackRef} = useAvailableWidthPx();
 *
 *   return (
 *     <div
 *       ref={callbackRef}
 *       style={{maxWidth: availableWidthPx > 0 ? availableWidthPx : undefined}}
 *     >
 *       {props.children}
 *     </div>
 *   );
 * }
 * ```
 */
export function useAvailableWidthPx(): UseAvailableWidthPxReturn {
  const [availableWidthPx, setAvailableWidthPx] = useState<number>(0);
  const lastWidthRef = useRef<number>(0);
  const cleanupRef = useRef<(() => void) | null>(null);

  /**
   * @mandatory Callback ref passed to a DOM element via `ref={callbackRef}`.
   * React calls callback refs on every render if the reference changes.
   * Without useCallback, each render would detach and reattach the ref,
   * disconnecting and recreating ResizeObservers on every render — a severe
   * resource leak.
   */
  const callbackRef = useCallback((node: HTMLDivElement | null) => {
    cleanupRef.current?.();
    cleanupRef.current = null;

    if (!node) {
      return;
    }

    const calculate = () => {
      const parent = node.parentElement;
      if (!parent) {
        return;
      }

      // Use getBoundingClientRect for both node and parent so the offset
      // is truly relative to the parent — not to the offsetParent which
      // can be an unrelated ancestor high up in the tree.
      const nodeLeft = node.getBoundingClientRect().left;
      const parentLeft = parent.getBoundingClientRect().left;
      const relativeLeft = nodeLeft - parentLeft;

      const width = calculateRemainingWidth(relativeLeft, parent.clientWidth);

      if (width > 0 && width !== lastWidthRef.current) {
        lastWidthRef.current = width;
        setAvailableWidthPx(width);
      }
    };

    calculate();

    const observer = new ResizeObserver(calculate);
    observer.observe(document.body);

    cleanupRef.current = () => {
      observer.disconnect();
    };
  }, []);

  return {availableWidthPx, callbackRef};
}

/**
 * Pure helper: compute remaining parent width from an element's relative left
 * offset within its parent.
 *
 * ```
 * remaining = parentClientWidth − relativeLeftInParent
 * ```
 *
 * The result is **floored** to whole pixels so that subpixel layout jitter
 * does not cause unnecessary React state updates.
 *
 * Exported for unit testing.  Not intended for direct use in components —
 * use {@link useAvailableWidthPx} instead.
 *
 * @param relativeLeftInParent - Distance in pixels from the element's left
 *   edge to its parent's left edge, computed via
 *   `node.getBoundingClientRect().left − parent.getBoundingClientRect().left`.
 * @param parentClientWidth - `element.parentElement.clientWidth` at
 *   measurement time.
 * @returns Whole CSS pixels remaining to the right of the element's left
 *   edge within its parent.  Always `>= 0`; returns `0` when
 *   `relativeLeftInParent >= parentClientWidth`.
 */
export function calculateRemainingWidth(
  relativeLeftInParent: number,
  parentClientWidth: number
): number {
  const remaining = parentClientWidth - relativeLeftInParent;
  return remaining > 0 ? Math.floor(remaining) : 0;
}
